import { Inject, Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
  HttpClient
} from '@angular/common/http';
import { catchError, Observable, throwError, switchMap } from 'rxjs';
import { AuthService, environment } from '@org/shared-services';
import { Router } from '@angular/router';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isLoggingOut = false;
  private readonly refreshUrl = `${environment.apiUrl}/api/users/refresh-token`;

  constructor(
    @Inject(AuthService) private authService: AuthService,
    private router: Router,
    private http: HttpClient
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const authRequest = request.clone({ withCredentials: true });

    return next.handle(authRequest).pipe(
      catchError((err) => {
        if (!(err instanceof HttpErrorResponse) || err.status !== 401) {
          return throwError(() => err);
        }

        const isAuthRoute =
          request.url.includes('/login') ||
          request.url.includes('/refresh-token') ||
          request.url.includes('/register');

        if (isAuthRoute || request.headers.has('X-Auth-Retry')) {
          return this.handleUnauthorized(request);
        }

        // Avoid refresh-token noise when there is no client session yet (e.g. initial /me).
        if (request.url.includes('/me') && !this.authService.isLoggedIn()) {
          return throwError(() => err);
        }

        return this.http
          .post(this.refreshUrl, {}, { withCredentials: true })
          .pipe(
            switchMap(() =>
              next.handle(
                request.clone({
                  withCredentials: true,
                  setHeaders: { 'X-Auth-Retry': '1' }
                })
              )
            ),
            catchError(() => this.handleUnauthorized(request))
          );
      })
    );
  }

  private handleUnauthorized(request?: HttpRequest<unknown>): Observable<never> {
    const sessionCritical =
      !request ||
      request.url.includes('/me') ||
      request.url.includes('/refresh-token');

    const withinLoginGrace =
      request?.url.includes('/me') &&
      Date.now() - this.authService.getLastLoginAt() < 8000;

    if (
      sessionCritical &&
      !withinLoginGrace &&
      this.authService.isLoggedIn() &&
      !this.isLoggingOut &&
      this.router.url !== '/login'
    ) {
      this.isLoggingOut = true;
      this.authService.clearSession();
      setTimeout(() => (this.isLoggingOut = false), 2000);
    }
    return throwError(() => new Error('Unauthorized'));
  }
}
